import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { playCourseUrl } from '../../../Infrastructure/Config/ApiConfig';
import { showToast } from '../../../Infrastructure/Notifications/ToastrConfig';
import { deleteCourseAsync, getCourseByIdAsync, updateCourseAsync, addModuleToCourseAsync, createAssessmentModuleAsync, createContentModuleAsync, getModuleTypesAsync, removeCourseModuleAsync } from '../../../Services/CourseService';
import Card from '../../../Shared/Components/Card/Card';
import Header from '../../../Shared/Components/Header/Header';
import Modal from '../../../Shared/Components/Modal/Modal';
import { DefaultEditor } from 'react-simple-wysiwyg';

const Course = () => {
    const createEmptyOption = () => ({
        id: `${Date.now()}-${Math.random()}`,
        text: '',
        isCorrect: false
    });

    const createEmptyQuestion = () => ({
        id: `${Date.now()}-${Math.random()}`,
        questionText: '',
        questionType: 'Multiple Choice',
        required: true,
        allowMultiple: false,
        placeholder: '',
        characterLimit: 300,
        ordinal: 1,
        options: [createEmptyOption(), createEmptyOption()]
    });

    const [course, setCourse] = useState({
        title: '', friendlyName: '', description: '', reference: '', hasAssessment: true,
        modules: [{ contentUrl: '', description: '', moduleType: '', ordinal: 0, title: '' }]
    });
    const { id } = useParams();
    const [editMode, setEditMode] = useState(false);
    const history = useHistory();
    const [uploading, setUploading] = useState(false);
    // Badge image shown on the learner's dashboard, certificate verify page and LinkedIn link
    const [badgeImage, setBadgeImage] = useState(null);
    const [errors, setErrors] = useState([]);
    const [moduleTypes, setModuleTypes] = useState([]);
    const [moduleTitle, setModuleTitle] = useState('');
    const [moduleDescription, setModuleDescription] = useState('');
    const [moduleTypeId, setModuleTypeId] = useState('');
    const [moduleFile, setModuleFile] = useState(null);
    const [moduleHtmlContent, setModuleHtmlContent] = useState('');
    const [moduleOrdinal, setModuleOrdinal] = useState('');
    const [assessmentQuestions, setAssessmentQuestions] = useState([createEmptyQuestion()]);
    const [isModuleSubmitting, setIsModuleSubmitting] = useState(false);
    const [moduleUploadProgress, setModuleUploadProgress] = useState(0);
    const [moduleUploadStatus, setModuleUploadStatus] = useState('');
    const [moduleToRemove, setModuleToRemove] = useState(null);

    const selectedModuleType = moduleTypes.find(type => String(type.id) === String(moduleTypeId));
    const selectedModuleTypeName = (selectedModuleType?.name || '').trim().toLowerCase();
    const isAssessmentModuleType = selectedModuleTypeName.includes('assessment');
    const isContentModuleType = selectedModuleTypeName === 'content';
    const isAudioModuleType = selectedModuleTypeName.includes('audio') || selectedModuleTypeName.includes('mp3');
    const isFileUploadModuleType = !!moduleTypeId && !isAssessmentModuleType && !isContentModuleType && !isAudioModuleType;

    const getModuleFileConfig = (moduleTypeName) => {
        if (moduleTypeName === 'xapi') {
            return {
                accept: '.zip,application/zip,application/x-zip-compressed',
                allowedExtensions: ['.zip'],
                helpText: 'Accepted file type: .zip'
            };
        }

        if (moduleTypeName === 'video') {
            return {
                accept: '.mp4,video/mp4',
                allowedExtensions: ['.mp4'],
                helpText: 'Accepted file type: .mp4'
            };
        }

        if (moduleTypeName === 'document') {
            return {
                accept: '.pdf,application/pdf',
                allowedExtensions: ['.pdf'],
                helpText: 'Accepted file type: .pdf'
            };
        }

        if (moduleTypeName.includes('audio') || moduleTypeName.includes('mp3')) {
            return {
                accept: '.mp3,audio/mpeg',
                allowedExtensions: ['.mp3'],
                helpText: 'Accepted file type: .mp3'
            };
        }

        return {
            accept: '',
            allowedExtensions: [],
            helpText: 'Select the required file for this module type'
        };
    };

    const isValidFileTypeForModule = (file, allowedExtensions) => {
        if (!file || !Array.isArray(allowedExtensions) || allowedExtensions.length === 0) {
            return true;
        }

        const lowerName = (file.name || '').toLowerCase();
        return allowedExtensions.some((extension) => lowerName.endsWith(extension));
    };

    const moduleFileConfig = getModuleFileConfig(selectedModuleTypeName);
    const isUploadModuleType = isFileUploadModuleType || isAudioModuleType;

    const resetModuleUploadState = () => {
        setIsModuleSubmitting(false);
        setModuleUploadProgress(0);
        setModuleUploadStatus('');
    };

    const closeModalSafely = (modalId) => {
        const modalElement = document.getElementById(modalId);
        if (!modalElement) {
            return;
        }

        try {
            modalElement.classList.remove('show');
            modalElement.style.display = 'none';
            modalElement.setAttribute('aria-hidden', 'true');
            modalElement.removeAttribute('aria-modal');
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('padding-right');
            document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove());
        } catch (error) {
            console.warn(`Unable to close modal ${modalId}`, error);
        }
    };

    const addAssessmentQuestion = () => {
        setAssessmentQuestions(previous => [
            ...previous,
            {
                ...createEmptyQuestion(),
                ordinal: previous.length + 1
            }
        ]);
    };

    const removeAssessmentQuestion = (questionId) => {
        setAssessmentQuestions(previous => {
            const filtered = previous.filter(question => question.id !== questionId);
            return filtered.map((question, index) => ({ ...question, ordinal: index + 1 }));
        });
    };

    const updateAssessmentQuestion = (questionId, field, value) => {
        setAssessmentQuestions(previous => previous.map(question => {
            if (question.id !== questionId) {
                return question;
            }

            return {
                ...question,
                [field]: value
            };
        }));
    };

    const addQuestionOption = (questionId) => {
        setAssessmentQuestions(previous => previous.map(question => {
            if (question.id !== questionId) {
                return question;
            }

            return {
                ...question,
                options: [...question.options, createEmptyOption()]
            };
        }));
    };

    const removeQuestionOption = (questionId, optionId) => {
        setAssessmentQuestions(previous => previous.map(question => {
            if (question.id !== questionId) {
                return question;
            }

            if (question.options.length <= 2) {
                return question;
            }

            return {
                ...question,
                options: question.options.filter(option => option.id !== optionId)
            };
        }));
    };

    const updateQuestionOption = (questionId, optionId, field, value) => {
        setAssessmentQuestions(previous => previous.map(question => {
            if (question.id !== questionId) {
                return question;
            }

            return {
                ...question,
                options: question.options.map(option => {
                    if (option.id !== optionId) {
                        return option;
                    }

                    return {
                        ...option,
                        [field]: value
                    };
                })
            };
        }));
    };

    useEffect(() => {
        const getCourse = async () => {
            let response = await getCourseByIdAsync(id);
            setCourse(response);
        }
        const getModuleTypes = async () => {
            let response = await getModuleTypesAsync();
            const types = Array.isArray(response?.moduleTypes)
                ? response.moduleTypes
                : (Array.isArray(response) ? response : []);

            setModuleTypes(types);
        }
        getCourse();
        getModuleTypes();
    }, [id, editMode]);

    const handleChange = evt => {
        const { name, value, type, checked } = evt.target;
        setCourse({ ...course, [name]: type === 'checkbox' ? checked : value });
    }

    const launchCourse = async () => {
        window.open(`${playCourseUrl()}preview/${course.id}`, course.title, 'toolbar=no,menubar=no,location=no')
    }

    const handleValidation = () => {
        let fields = course;
        let errors = {};
        let formIsValid = true;

        //title
        if (!fields["title"]) {
            formIsValid = false;
            errors["title"] = "Please enter a title";
        }

        //description
        if (!fields["description"]) {
            formIsValid = false;
            errors["description"] = "Please enter a description";
        }

        //reference
        if (!fields["reference"]) {
            formIsValid = false;
            errors["reference"] = "Please add a reference";
        }

        setErrors(errors);
        return formIsValid;
    }

    const updateCourse = async () => {
        if (handleValidation()) {
            setUploading(true);
            let form = new FormData();
            form.append("CourseId", course.id);
            form.append("Reference", course.reference);
            form.append("Title", course.title);
            form.append("FriendlyName", course.friendlyName ?? '');
            form.append("Description", course.description);
            form.append("HasAssessment", course.hasAssessment ?? true);
            if (badgeImage) {
                form.append("BadgeImage", badgeImage);
            }


            let response = await updateCourseAsync(form);

            if (response.success) {
                showToast(true, "Course updated");
                setEditMode(false);
                setUploading(false);
                if (badgeImage) {
                    // Reload to show the newly uploaded badge
                    setBadgeImage(null);
                    setCourse(await getCourseByIdAsync(id));
                }
            }
            else {
                showToast(false, "Something went wrong");
            }
        }
    }

    const deleteCourse = async () => {
        let data = {
            courseId: id
        }

        let response = await deleteCourseAsync(data);
        if (response) {
            showToast(true, "Course deleted");
            history.goBack();
        }
        else {
            showToast(false, "Something went wrong");
        }
    }

    const handleRemoveModule = async () => {
        if (!moduleToRemove) {
            return;
        }

        try {
            const response = await removeCourseModuleAsync({
                courseId: course.id,
                moduleId: moduleToRemove.id
            });

            if (response && response.success !== false) {
                showToast(true, 'Module removed');
                // Refresh the course so the module list reflects the removal.
                const refreshed = await getCourseByIdAsync(id);
                setCourse(refreshed);
            } else {
                showToast(false, response?.errors?.[0]?.error || 'Failed to remove module');
            }
        } catch (error) {
            console.error('Error removing module:', error);
            showToast(false, error?.response?.data?.errors?.[0]?.error || 'Failed to remove module');
        } finally {
            setModuleToRemove(null);
        }
    }

    const handleCreateModule = async () => {
        if (isModuleSubmitting) {
            return;
        }

        if (!moduleTitle.trim()) {
            showToast(false, 'Module title is required');
            return;
        }
        if (!moduleDescription.trim()) {
            showToast(false, 'Module description is required');
            return;
        }
        if (!moduleTypeId) {
            showToast(false, 'Please select a module type');
            return;
        }

        if (isUploadModuleType && !moduleFile) {
            showToast(false, 'Please select a file to upload');
            return;
        }

        if (isUploadModuleType && !isValidFileTypeForModule(moduleFile, moduleFileConfig.allowedExtensions)) {
            showToast(false, moduleFileConfig.helpText);
            return;
        }

        if (isContentModuleType && !(moduleHtmlContent || '').trim()) {
            showToast(false, 'HTML content is required for content modules');
            return;
        }

        if (isAssessmentModuleType) {
            const hasEmptyQuestion = assessmentQuestions.some(question => !(question.questionText || '').trim());
            if (hasEmptyQuestion) {
                showToast(false, 'All assessment questions must have question text');
                return;
            }

            const invalidChoiceQuestions = assessmentQuestions.some(question => {
                const type = (question.questionType || '').toLowerCase();
                if (!type.includes('multiple') && !type.includes('mixed')) {
                    return false;
                }

                const validOptions = question.options.filter(option => (option.text || '').trim() !== '');
                return validOptions.length < 2;
            });

            if (invalidChoiceQuestions) {
                showToast(false, 'Multiple choice and mixed questions must have at least two options');
                return;
            }
        }

        setIsModuleSubmitting(true);
        setModuleUploadProgress(0);
        setModuleUploadStatus(isUploadModuleType ? 'Preparing upload...' : 'Creating module...');

        try {
            let response;

            if (isAssessmentModuleType) {
                const mappedQuestions = assessmentQuestions.map((question, index) => {
                    const type = (question.questionType || '').toLowerCase();
                    const options = (question.options || [])
                        .filter(option => (option.text || '').trim() !== '')
                        .map((option, optionIndex) => ({
                            text: option.text,
                            ordinal: optionIndex + 1,
                            isCorrect: option.isCorrect
                        }));

                    return {
                        text: question.questionText,
                        questionType: question.questionType,
                        ordinal: index + 1,
                        options: (type.includes('multiple') || type.includes('mixed')) ? options : []
                    };
                });

                const data = {
                    courseId: id,
                    title: moduleTitle,
                    description: moduleDescription,
                    ordinal: moduleOrdinal ? Number(moduleOrdinal) : null,
                    questions: mappedQuestions
                };

                response = await createAssessmentModuleAsync(data);
            } else if (isContentModuleType) {
                const data = {
                    courseId: id,
                    title: moduleTitle,
                    description: moduleDescription,
                    htmlContent: moduleHtmlContent,
                    ordinal: moduleOrdinal ? Number(moduleOrdinal) : null
                };

                response = await createContentModuleAsync(data);
            } else {
                let formData = new FormData();
                formData.append('courseId', id);
                formData.append('moduleTypeId', moduleTypeId);
                formData.append('title', moduleTitle);
                formData.append('description', moduleDescription);
                formData.append('isRequired', true);

                if (moduleOrdinal) {
                    formData.append('ordinal', moduleOrdinal);
                }

                formData.append('file', moduleFile);
                response = await addModuleToCourseAsync(formData, {
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent?.phase === 'preparing') {
                            setModuleUploadProgress(0);
                            setModuleUploadStatus('Preparing secure upload...');
                            return;
                        }

                        if (progressEvent?.phase === 'processing') {
                            setModuleUploadProgress(95);
                            setModuleUploadStatus('Finalising upload...');
                            return;
                        }

                        if (progressEvent?.phase === 'complete') {
                            setModuleUploadProgress(100);
                            setModuleUploadStatus('Upload complete');
                            return;
                        }

                        if (!progressEvent?.total) {
                            setModuleUploadStatus('Uploading module...');
                            return;
                        }

                        const rawProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        const progress = Math.min(90, rawProgress);
                        setModuleUploadProgress(progress);
                        setModuleUploadStatus(`Uploading directly to storage... ${progress}%`);
                    }
                });
            }

            if (response && (response.success || response.id || response.moduleId)) {
                if (isUploadModuleType) {
                    setModuleUploadProgress(100);
                    setModuleUploadStatus('Upload complete');
                }

                showToast(true, 'Module added successfully');
                setModuleTitle('');
                setModuleDescription('');
                setModuleTypeId('');
                setModuleFile(null);
                setModuleHtmlContent('');
                setModuleOrdinal('');
                setAssessmentQuestions([createEmptyQuestion()]);
                setEditMode(false);
                resetModuleUploadState();
                closeModalSafely('createModuleModal');
            } else {
                resetModuleUploadState();
                showToast(false, response?.errors?.[0]?.error || response?.message || 'Failed to add module');
            }
        } catch (error) {
            console.error('Module upload failed', error);
            resetModuleUploadState();
            showToast(false, error?.response?.data?.errors?.[0]?.error || error?.response?.data?.message || error?.message || 'Failed to add module');
        }
    }

    return (
        <>
            <Modal buttonFunc={deleteCourse}
                modalId={"deleteCourse"}
                bodyText="Are you sure you want to delete this course?"
                title={`Delete ${course.title}`}
                buttonText="Delete"
            />
            <Modal buttonFunc={handleRemoveModule}
                modalId={"removeModuleModal"}
                bodyText={
                    <div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            background: '#fef2f2',
                            border: '2px solid #ef4444',
                            borderRadius: '10px',
                            padding: '16px',
                            marginBottom: '16px'
                        }}>
                            <span style={{ fontSize: '1.75rem', lineHeight: 1 }}>⚠️</span>
                            <div style={{ fontWeight: '700', color: '#b91c1c', fontSize: '1.05rem' }}>
                                Destructive action — this cannot be undone
                            </div>
                        </div>
                        <p style={{ marginBottom: '12px', color: '#1f2937' }}>
                            You are about to permanently remove the module{' '}
                            <strong>"{moduleToRemove?.title || ''}"</strong> from this course.
                        </p>
                        <p style={{ marginBottom: '12px', color: '#1f2937', fontWeight: '600' }}>
                            This will also remove the module from <u>every learner</u> it was assigned to, including:
                        </p>
                        <ul style={{ color: '#4b5563', marginBottom: '16px', paddingLeft: '20px' }}>
                            <li>Their progress and completion status for this module</li>
                            <li>Any assessment responses they submitted for this module</li>
                        </ul>
                        <p style={{ color: '#b91c1c', fontWeight: '700' }}>
                            There is no way to recover this data once removed. Are you absolutely sure?
                        </p>
                    </div>
                }
                title="Remove Module"
                buttonText="Yes, permanently remove"
            />
            <Header heading={course.title} subheading={course.reference} icon="book" />
            <div className="container-fluid content">
                <Card title="Course Details">
                    <div style={{ padding: '20px' }}>
                        {/* Display Mode */}
                        {!editMode ? (
                            <>
                                <div style={{
                                    display: 'grid',
                                    gap: '24px',
                                    marginBottom: '32px'
                                }}>
                                    {/* Title Display */}
                                    <div style={{
                                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        borderLeft: '4px solid #667eea'
                                    }}>
                                        <div style={{
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '8px'
                                        }}>
                                            📚 Course Title
                                        </div>
                                        <div style={{
                                            fontSize: '1.25rem',
                                            fontWeight: '600',
                                            color: '#1f2937'
                                        }}>
                                            {course.title}
                                        </div>
                                    </div>

                                    {/* Reference Display */}
                                    <div style={{
                                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        borderLeft: '4px solid #11998e'
                                    }}>
                                        <div style={{
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '8px'
                                        }}>
                                            🔖 Reference Code
                                        </div>
                                        <div style={{
                                            fontSize: '1.1rem',
                                            fontWeight: '600',
                                            color: '#1f2937',
                                            fontFamily: 'monospace'
                                        }}>
                                            {course.reference}
                                        </div>
                                    </div>

                                    {/* Friendly Name Display */}
                                    <div style={{
                                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        borderLeft: '4px solid #8b5cf6'
                                    }}>
                                        <div style={{
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '8px'
                                        }}>
                                            ✨ Friendly Name
                                        </div>
                                        <div style={{
                                            fontSize: '1.1rem',
                                            fontWeight: '600',
                                            color: '#1f2937'
                                        }}>
                                            {course.friendlyName || '-'}
                                        </div>
                                    </div>

                                    {/* Description Display */}
                                    <div style={{
                                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        borderLeft: '4px solid #4facfe'
                                    }}>
                                        <div style={{
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '8px'
                                        }}>
                                            📝 Description
                                        </div>
                                        <div style={{
                                            fontSize: '0.95rem',
                                            color: '#4b5563',
                                            lineHeight: '1.6'
                                        }}>
                                            {course.description}
                                        </div>
                                    </div>

                                    <div style={{
                                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                                        borderRadius: '12px',
                                        padding: '20px',
                                        borderLeft: '4px solid #0ea5e9'
                                    }}>
                                        <div style={{
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: '8px'
                                        }}>
                                            Assessment Mode
                                        </div>
                                        <div style={{
                                            fontSize: '1.1rem',
                                            fontWeight: '600',
                                            color: '#1f2937'
                                        }}>
                                            {course.hasAssessment ? 'Assessment Course' : 'Mindfullness Course'}
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons - Display Mode */}
                                <div style={{
                                    display: 'flex',
                                    gap: '12px',
                                    justifyContent: 'flex-end',
                                    flexWrap: 'wrap'
                                }}>
                                    <button
                                        onClick={launchCourse}
                                        style={{
                                            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '12px 24px',
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer',
                                            boxShadow: '0 4px 12px rgba(67, 231, 123, 0.3)',
                                            transition: 'all 0.3s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(67, 231, 123, 0.4)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(67, 231, 123, 0.3)';
                                        }}
                                    >
                                        ▶️ Preview Course
                                    </button>
                                    <button
                                        onClick={() => setEditMode(true)}
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '12px 24px',
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer',
                                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                            transition: 'all 0.3s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                                        }}
                                    >
                                        ✏️ Edit Details
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* Edit Mode */
                            <>
                                <div style={{
                                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    marginBottom: '24px',
                                    border: '2px solid #fbbf24',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}>
                                    <div style={{ fontSize: '1.5rem' }}>✏️</div>
                                    <div>
                                        <div style={{ fontWeight: '600', color: '#92400e', fontSize: '0.95rem' }}>
                                            Edit Mode
                                        </div>
                                        <div style={{ color: '#78350f', fontSize: '0.85rem' }}>
                                            Make changes to your course details below
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gap: '20px', marginBottom: '32px' }}>
                                    {/* Title Input */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#374151',
                                            marginBottom: '8px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            📚 Course Title
                                        </label>
                                        <input
                                            onChange={handleChange}
                                            name="title"
                                            type="text"
                                            value={course.title}
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                borderRadius: '8px',
                                                border: errors.title ? '2px solid #ef4444' : '2px solid #e5e7eb',
                                                fontSize: '1rem',
                                                transition: 'all 0.2s ease',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => {
                                                if (!errors.title) e.currentTarget.style.borderColor = '#667eea';
                                            }}
                                            onBlur={(e) => {
                                                if (!errors.title) e.currentTarget.style.borderColor = '#e5e7eb';
                                            }}
                                        />
                                        {errors.title && (
                                            <small style={{
                                                display: 'block',
                                                marginTop: '6px',
                                                color: '#ef4444',
                                                fontSize: '0.85rem',
                                                fontWeight: '500'
                                            }}>
                                                ⚠️ {errors.title}
                                            </small>
                                        )}
                                    </div>

                                    {/* Reference Input */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#374151',
                                            marginBottom: '8px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            🔖 Reference Code
                                        </label>
                                        <input
                                            onChange={handleChange}
                                            name="reference"
                                            value={course.reference}
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                borderRadius: '8px',
                                                border: errors.reference ? '2px solid #ef4444' : '2px solid #e5e7eb',
                                                fontSize: '1rem',
                                                fontFamily: 'monospace',
                                                transition: 'all 0.2s ease',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => {
                                                if (!errors.reference) e.currentTarget.style.borderColor = '#11998e';
                                            }}
                                            onBlur={(e) => {
                                                if (!errors.reference) e.currentTarget.style.borderColor = '#e5e7eb';
                                            }}
                                        />
                                        {errors.reference && (
                                            <small style={{
                                                display: 'block',
                                                marginTop: '6px',
                                                color: '#ef4444',
                                                fontSize: '0.85rem',
                                                fontWeight: '500'
                                            }}>
                                                ⚠️ {errors.reference}
                                            </small>
                                        )}
                                    </div>

                                    {/* Friendly Name Input */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#374151',
                                            marginBottom: '8px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            ✨ Friendly Name
                                        </label>
                                        <input
                                            onChange={handleChange}
                                            name="friendlyName"
                                            value={course.friendlyName || ''}
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                borderRadius: '8px',
                                                border: '2px solid #e5e7eb',
                                                fontSize: '1rem',
                                                transition: 'all 0.2s ease',
                                                outline: 'none'
                                            }}
                                            onFocus={(e) => {
                                                e.currentTarget.style.borderColor = '#8b5cf6';
                                            }}
                                            onBlur={(e) => {
                                                e.currentTarget.style.borderColor = '#e5e7eb';
                                            }}
                                        />
                                    </div>

                                    {/* Description Input */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#374151',
                                            marginBottom: '8px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            📝 Description
                                        </label>
                                        <textarea
                                            onChange={handleChange}
                                            name="description"
                                            value={course.description}
                                            style={{
                                                width: '100%',
                                                minHeight: '150px',
                                                padding: '12px 16px',
                                                borderRadius: '8px',
                                                border: errors.description ? '2px solid #ef4444' : '2px solid #e5e7eb',
                                                fontSize: '0.95rem',
                                                lineHeight: '1.6',
                                                transition: 'all 0.2s ease',
                                                outline: 'none',
                                                resize: 'vertical'
                                            }}
                                            onFocus={(e) => {
                                                if (!errors.description) e.currentTarget.style.borderColor = '#4facfe';
                                            }}
                                            onBlur={(e) => {
                                                if (!errors.description) e.currentTarget.style.borderColor = '#e5e7eb';
                                            }}
                                        />
                                        {errors.description && (
                                            <small style={{
                                                display: 'block',
                                                marginTop: '6px',
                                                color: '#ef4444',
                                                fontSize: '0.85rem',
                                                fontWeight: '500'
                                            }}>
                                                ⚠️ {errors.description}
                                            </small>
                                        )}
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#374151',
                                            marginBottom: '8px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Badge Image
                                        </label>
                                        {course.badgeImageUrl && !badgeImage && (
                                            <img
                                                alt="Current badge"
                                                src={course.badgeImageUrl}
                                                style={{ width: '96px', display: 'block', marginBottom: '8px' }}
                                            />
                                        )}
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/gif"
                                            onChange={e => setBadgeImage(e.target.files[0] || null)}
                                        />
                                        <small style={{ display: 'block', marginTop: '6px', color: '#6b7280' }}>
                                            Shown on learners' dashboards and their certificate verification page. PNG, JPG or GIF.
                                        </small>
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#374151',
                                            marginBottom: '8px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Assessment Mode
                                        </label>
                                        <label style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            fontSize: '0.95rem',
                                            color: '#1f2937',
                                            cursor: 'pointer'
                                        }}>
                                            <input
                                                type="checkbox"
                                                onChange={handleChange}
                                                name="hasAssessment"
                                                checked={!!course.hasAssessment}
                                            />
                                            This course includes assessments
                                        </label>
                                    </div>
                                </div>

                                {/* Action Buttons - Edit Mode */}
                                <div style={{
                                    display: 'flex',
                                    gap: '12px',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap'
                                }}>
                                    <button
                                        data-toggle="modal"
                                        data-target="#deleteCourse"
                                        style={{
                                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '12px 24px',
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '0.95rem',
                                            cursor: 'pointer',
                                            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                                        }}
                                    >
                                        🗑️ Delete Course
                                    </button>

                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <button
                                            onClick={() => setEditMode(false)}
                                            style={{
                                                background: '#f3f4f6',
                                                border: '2px solid #d1d5db',
                                                borderRadius: '8px',
                                                padding: '12px 24px',
                                                color: '#6b7280',
                                                fontWeight: '600',
                                                fontSize: '0.95rem',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = '#e5e7eb';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = '#f3f4f6';
                                            }}
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            onClick={updateCourse}
                                            disabled={uploading}
                                            style={{
                                                background: uploading
                                                    ? '#9ca3af'
                                                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '12px 24px',
                                                color: 'white',
                                                fontWeight: '600',
                                                fontSize: '0.95rem',
                                                cursor: uploading ? 'not-allowed' : 'pointer',
                                                boxShadow: uploading ? 'none' : '0 4px 12px rgba(16, 185, 129, 0.3)',
                                                transition: 'all 0.3s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!uploading) {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!uploading) {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                                                }
                                            }}
                                        >
                                            {uploading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>💾 Save Changes</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </Card>
                <Card title="Course Journey">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '20px 20px 0' }}>
                        <button
                            data-toggle="modal"
                            data-target="#createModuleModal"
                            style={{
                                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(17, 153, 142, 0.25)',
                                transition: 'all 0.3s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(17, 153, 142, 0.35)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(17, 153, 142, 0.25)';
                            }}
                        >
                            + Add Module
                        </button>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            {course.modules && course.modules.length > 0 ? (
                                <div style={{ padding: '20px' }}>
                                    {/* Module Flow */}
                                    <div style={{ position: 'relative' }}>
                                        {course.modules
                                            .sort((a, b) => a.ordinal - b.ordinal)
                                            .map((module, index) => {
                                                const isFirst = index === 0;
                                                const isLast = index === course.modules.length - 1;
                                                const gradients = [
                                                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                                    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                                    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                                                    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                                                ];
                                                const gradient = gradients[index % gradients.length];

                                                return (
                                                    <div key={module.id || index} style={{ position: 'relative' }}>
                                                        {/* Module Card */}
                                                        <div style={{
                                                            display: 'flex',
                                                            gap: '20px',
                                                            marginBottom: isLast ? '0' : '24px',
                                                            position: 'relative'
                                                        }}>
                                                            {/* Ordinal Badge */}
                                                            <div style={{
                                                                flexShrink: 0,
                                                                width: '50px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center'
                                                            }}>
                                                                <div style={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    background: gradient,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: 'white',
                                                                    fontWeight: 'bold',
                                                                    fontSize: '1.2rem',
                                                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                                    position: 'relative',
                                                                    zIndex: 2
                                                                }}>
                                                                    {module.ordinal || index + 1}
                                                                </div>
                                                                {!isLast && (
                                                                    <div style={{
                                                                        width: '3px',
                                                                        flex: 1,
                                                                        background: '#e5e7eb',
                                                                        minHeight: '40px',
                                                                        marginTop: '8px',
                                                                        marginBottom: '8px',
                                                                        position: 'relative',
                                                                        zIndex: 1
                                                                    }}>
                                                                        <div style={{
                                                                            position: 'absolute',
                                                                            bottom: '-8px',
                                                                            left: '50%',
                                                                            transform: 'translateX(-50%)',
                                                                            width: '0',
                                                                            height: '0',
                                                                            borderLeft: '6px solid transparent',
                                                                            borderRight: '6px solid transparent',
                                                                            borderTop: '8px solid #e5e7eb'
                                                                        }}></div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Module Content */}
                                                            <div style={{
                                                                flex: 1,
                                                                background: 'white',
                                                                border: '2px solid #e5e7eb',
                                                                borderRadius: '12px',
                                                                padding: '20px',
                                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                                                                transition: 'all 0.3s ease',
                                                                cursor: 'pointer'
                                                            }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.borderColor = '#667eea';
                                                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.15)';
                                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                                }}>
                                                                <div style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'start',
                                                                    marginBottom: '12px'
                                                                }}>
                                                                    <div>
                                                                        <h6 style={{
                                                                            margin: 0,
                                                                            color: '#1f2937',
                                                                            fontSize: '1.1rem',
                                                                            fontWeight: '600',
                                                                            marginBottom: '4px'
                                                                        }}>
                                                                            {module.title}
                                                                        </h6>
                                                                        {isFirst && (
                                                                            <span style={{
                                                                                fontSize: '0.75rem',
                                                                                color: '#10b981',
                                                                                fontWeight: '600',
                                                                                background: '#d1fae5',
                                                                                padding: '2px 8px',
                                                                                borderRadius: '4px',
                                                                                marginRight: '8px'
                                                                            }}>
                                                                                START HERE
                                                                            </span>
                                                                        )}
                                                                        {isLast && (
                                                                            <span style={{
                                                                                fontSize: '0.75rem',
                                                                                color: '#8b5cf6',
                                                                                fontWeight: '600',
                                                                                background: '#ede9fe',
                                                                                padding: '2px 8px',
                                                                                borderRadius: '4px'
                                                                            }}>
                                                                                FINAL MODULE
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <span style={{
                                                                            fontSize: '0.8rem',
                                                                            color: 'white',
                                                                            background: gradient,
                                                                            padding: '4px 12px',
                                                                            borderRadius: '6px',
                                                                            fontWeight: '600'
                                                                        }}>
                                                                            {module.moduleType}
                                                                        </span>
                                                                        <button
                                                                            data-toggle="modal"
                                                                            data-target="#removeModuleModal"
                                                                            onClick={() => setModuleToRemove(module)}
                                                                            title="Remove module"
                                                                            style={{
                                                                                background: 'transparent',
                                                                                border: '1px solid #ef4444',
                                                                                color: '#ef4444',
                                                                                borderRadius: '6px',
                                                                                padding: '4px 10px',
                                                                                fontSize: '0.8rem',
                                                                                fontWeight: '600',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                        >
                                                                            🗑️ Remove
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <p style={{
                                                                    margin: 0,
                                                                    color: '#6b7280',
                                                                    fontSize: '0.9rem',
                                                                    lineHeight: '1.6',
                                                                    marginBottom: '12px'
                                                                }}>
                                                                    {module.description}
                                                                </p>
                                                                {module.contentUrl ? (
                                                                    <a
                                                                        href={module.contentUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '6px',
                                                                            fontSize: '0.85rem',
                                                                            color: '#667eea',
                                                                            textDecoration: 'none',
                                                                            fontWeight: '600'
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        📎 View Content
                                                                    </a>
                                                                ) : (
                                                                    <span style={{
                                                                        fontSize: '0.85rem',
                                                                        color: '#9ca3af',
                                                                        fontStyle: 'italic'
                                                                    }}>
                                                                        No content available
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <p className="text-muted">No modules available for this course</p>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>
            </div>

            {/* Create Module Modal */}
            <Modal
                modalId="createModuleModal"
                title="Add New Module"
                bodyText={
                    <div style={{ padding: '20px 0' }}>
                        {isModuleSubmitting && (
                            <div style={{
                                marginBottom: '20px',
                                padding: '16px',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #ecfeff 0%, #f0fdf4 100%)',
                                border: '1px solid #99f6e4'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: isUploadModuleType ? '12px' : 0,
                                    color: '#0f766e',
                                    fontWeight: '600'
                                }}>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    <span>{moduleUploadStatus || 'Uploading module...'}</span>
                                </div>
                                {isUploadModuleType && (
                                    <>
                                        <div style={{
                                            height: '10px',
                                            borderRadius: '999px',
                                            backgroundColor: '#ccfbf1',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                width: `${moduleUploadProgress}%`,
                                                height: '100%',
                                                borderRadius: '999px',
                                                background: 'linear-gradient(135deg, #14b8a6 0%, #22c55e 100%)',
                                                transition: 'width 0.2s ease'
                                            }} />
                                        </div>
                                        <div style={{
                                            marginTop: '8px',
                                            fontSize: '0.85rem',
                                            color: '#0f766e'
                                        }}>
                                            {moduleUploadProgress > 0 ? `${moduleUploadProgress}% uploaded` : 'Upload starting...'}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        <div style={{
                            opacity: isModuleSubmitting ? 0.65 : 1,
                            pointerEvents: isModuleSubmitting ? 'none' : 'auto',
                            transition: 'opacity 0.2s ease'
                        }}>
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{
                                    fontWeight: '600',
                                    marginBottom: '10px',
                                    display: 'block',
                                    color: '#1e293b',
                                    fontSize: '0.95rem'
                                }}>
                                    Module Type <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <select
                                    value={moduleTypeId}
                                    onChange={(e) => {
                                        setModuleTypeId(e.target.value);
                                        setModuleFile(null);
                                        setModuleHtmlContent('');
                                    }}
                                    style={{
                                        width: '100%',
                                        height: '48px',
                                        fontSize: '1rem',
                                        borderRadius: '10px',
                                        border: '2px solid #e2e8f0',
                                        padding: '0 16px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease',
                                        fontFamily: 'inherit',
                                        backgroundColor: 'white',
                                        cursor: 'pointer'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#11998e';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(17, 153, 142, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e2e8f0';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                >
                                    <option value="">Select module type...</option>
                                    {moduleTypes.map((type) => (
                                        <option key={type.id} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{
                                    fontWeight: '600',
                                    marginBottom: '10px',
                                    display: 'block',
                                    color: '#1e293b',
                                    fontSize: '0.95rem'
                                }}>
                                    Module Title <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter module title"
                                    value={moduleTitle}
                                    onChange={(e) => setModuleTitle(e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '48px',
                                        fontSize: '1rem',
                                        borderRadius: '10px',
                                        border: '2px solid #e2e8f0',
                                        padding: '0 16px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease',
                                        fontFamily: 'inherit'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#11998e';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(17, 153, 142, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e2e8f0';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <label style={{
                                    fontWeight: '600',
                                    marginBottom: '10px',
                                    display: 'block',
                                    color: '#1e293b',
                                    fontSize: '0.95rem'
                                }}>
                                    Module Description <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <textarea
                                    placeholder="Enter module description"
                                    value={moduleDescription}
                                    onChange={(e) => setModuleDescription(e.target.value)}
                                    rows="4"
                                    style={{
                                        width: '100%',
                                        fontSize: '1rem',
                                        borderRadius: '10px',
                                        border: '2px solid #e2e8f0',
                                        padding: '12px 16px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease',
                                        resize: 'vertical',
                                        fontFamily: 'inherit',
                                        lineHeight: '1.5'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#11998e';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(17, 153, 142, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e2e8f0';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                            </div>
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{
                                    fontWeight: '600',
                                    marginBottom: '10px',
                                    display: 'block',
                                    color: '#1e293b',
                                    fontSize: '0.95rem'
                                }}>
                                    Module Position (Optional)
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    placeholder="Leave empty to append at end"
                                    value={moduleOrdinal}
                                    onChange={(e) => setModuleOrdinal(e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '48px',
                                        fontSize: '1rem',
                                        borderRadius: '10px',
                                        border: '2px solid #e2e8f0',
                                        padding: '0 16px',
                                        outline: 'none',
                                        transition: 'all 0.3s ease',
                                        fontFamily: 'inherit'
                                    }}
                                />
                            </div>

                            {isContentModuleType && (
                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{
                                        fontWeight: '600',
                                        marginBottom: '10px',
                                        display: 'block',
                                        color: '#1e293b',
                                        fontSize: '0.95rem'
                                    }}>
                                        HTML Content <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <div style={{
                                        borderRadius: '10px',
                                        border: '2px solid #e2e8f0',
                                        overflow: 'hidden',
                                        backgroundColor: 'white'
                                    }}>
                                        <DefaultEditor
                                            value={moduleHtmlContent}
                                            onChange={(e) => setModuleHtmlContent(e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            {isAssessmentModuleType && (
                                <div style={{ marginBottom: '24px' }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '12px'
                                    }}>
                                        <label style={{
                                            fontWeight: '600',
                                            marginBottom: 0,
                                            color: '#1e293b',
                                            fontSize: '0.95rem'
                                        }}>
                                            Assessment Questions <span style={{ color: '#ef4444' }}>*</span>
                                        </label>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={addAssessmentQuestion}
                                        >
                                            + Add Question
                                        </button>
                                    </div>

                                    {assessmentQuestions.map((question, questionIndex) => {
                                        const questionType = (question.questionType || '').toLowerCase();
                                        const requiresOptions = questionType.includes('multiple') || questionType.includes('mixed');
                                        const showsFreeText = questionType.includes('free') || questionType.includes('mixed');

                                        return (
                                            <div
                                                key={question.id}
                                                style={{
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '10px',
                                                    padding: '12px',
                                                    marginBottom: '12px',
                                                    backgroundColor: '#f8fafc'
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                    <strong>Question {questionIndex + 1}</strong>
                                                    {assessmentQuestions.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => removeAssessmentQuestion(question.id)}
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="form-group">
                                                    <label>Question Text</label>
                                                    <textarea
                                                        className="form-control"
                                                        rows="2"
                                                        value={question.questionText}
                                                        onChange={(e) => updateAssessmentQuestion(question.id, 'questionText', e.target.value)}
                                                    />
                                                </div>

                                                <div className="row">
                                                    <div className="col-md-5 form-group">
                                                        <label>Question Type</label>
                                                        <select
                                                            className="form-control"
                                                            value={question.questionType}
                                                            onChange={(e) => updateAssessmentQuestion(question.id, 'questionType', e.target.value)}
                                                        >
                                                            <option>Multiple Choice</option>
                                                            <option>Free Text</option>
                                                            <option>Mixed</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-md-3 form-group">
                                                        <label>Required</label>
                                                        <div>
                                                            <input
                                                                type="checkbox"
                                                                checked={question.required}
                                                                onChange={(e) => updateAssessmentQuestion(question.id, 'required', e.target.checked)}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4 form-group">
                                                        <label>Allow Multiple</label>
                                                        <div>
                                                            <input
                                                                type="checkbox"
                                                                checked={question.allowMultiple}
                                                                disabled={!requiresOptions}
                                                                onChange={(e) => updateAssessmentQuestion(question.id, 'allowMultiple', e.target.checked)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {requiresOptions && (
                                                    <div style={{ marginBottom: '10px' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                            <label style={{ marginBottom: 0 }}>Options</label>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={() => addQuestionOption(question.id)}
                                                            >
                                                                + Add Option
                                                            </button>
                                                        </div>

                                                        {(question.options || []).map((option) => (
                                                            <div key={option.id} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Option text"
                                                                    value={option.text}
                                                                    onChange={(e) => updateQuestionOption(question.id, option.id, 'text', e.target.value)}
                                                                />
                                                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={option.isCorrect}
                                                                        onChange={(e) => updateQuestionOption(question.id, option.id, 'isCorrect', e.target.checked)}
                                                                    />
                                                                    Correct
                                                                </label>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => removeQuestionOption(question.id, option.id)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {showsFreeText && (
                                                    <div className="row">
                                                        <div className="col-md-8 form-group">
                                                            <label>Free Text Placeholder</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={question.placeholder}
                                                                onChange={(e) => updateAssessmentQuestion(question.id, 'placeholder', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="col-md-4 form-group">
                                                            <label>Character Limit</label>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                className="form-control"
                                                                value={question.characterLimit}
                                                                onChange={(e) => updateAssessmentQuestion(question.id, 'characterLimit', Number(e.target.value || 0))}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {(isFileUploadModuleType || isAudioModuleType) && (
                                <div>
                                    <label style={{
                                        fontWeight: '600',
                                        marginBottom: '10px',
                                        display: 'block',
                                        color: '#1e293b',
                                        fontSize: '0.95rem'
                                    }}>
                                        {isAudioModuleType ? 'Upload MP3 File' : 'Upload File'} <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <div style={{
                                        position: 'relative',
                                        borderRadius: '10px',
                                        border: '2px dashed #e2e8f0',
                                        padding: '24px',
                                        textAlign: 'center',
                                        transition: 'all 0.3s ease',
                                        backgroundColor: '#f9fafb',
                                        cursor: 'pointer'
                                    }}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.currentTarget.style.borderColor = '#11998e';
                                            e.currentTarget.style.backgroundColor = '#f0fdf4';
                                        }}
                                        onDragLeave={(e) => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.backgroundColor = '#f9fafb';
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.backgroundColor = '#f9fafb';
                                            if (e.dataTransfer.files.length > 0) {
                                                const file = e.dataTransfer.files[0];
                                                if (!isValidFileTypeForModule(file, moduleFileConfig.allowedExtensions)) {
                                                    showToast(false, moduleFileConfig.helpText);
                                                    return;
                                                }

                                                setModuleFile(file);
                                            }
                                        }}>
                                        <input
                                            type="file"
                                            accept={moduleFileConfig.accept}
                                            onChange={(e) => {
                                                if (e.target.files.length > 0) {
                                                    const file = e.target.files[0];
                                                    if (!isValidFileTypeForModule(file, moduleFileConfig.allowedExtensions)) {
                                                        showToast(false, moduleFileConfig.helpText);
                                                        return;
                                                    }

                                                    setModuleFile(file);
                                                }
                                            }}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                opacity: 0,
                                                cursor: 'pointer'
                                            }}
                                        />
                                        <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📁</div>
                                        {moduleFile ? (
                                            <div>
                                                <div style={{
                                                    fontSize: '0.95rem',
                                                    fontWeight: '600',
                                                    color: '#11998e',
                                                    marginBottom: '4px'
                                                }}>
                                                    {moduleFile.name}
                                                </div>
                                                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                                    {(moduleFile.size / 1024 / 1024).toFixed(2)} MB
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div style={{
                                                    fontSize: '0.95rem',
                                                    fontWeight: '600',
                                                    color: '#374151',
                                                    marginBottom: '4px'
                                                }}>
                                                    Click to upload or drag and drop
                                                </div>
                                                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                                    {moduleFileConfig.helpText}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                }
                buttonText={isModuleSubmitting
                    ? (isUploadModuleType
                        ? (moduleUploadProgress > 0 ? `Uploading ${moduleUploadProgress}%` : 'Uploading...')
                        : 'Creating...')
                    : 'Add Module'}
                buttonFunc={handleCreateModule}
                closeOnConfirm={false}
                disableConfirm={isModuleSubmitting}
                disableClose={isModuleSubmitting}
            />
        </>
    );
};

export default Course;